import { BasePaginateResultSchema } from '@/api/common.ts'
import enumKeys from '@/lib/enum'
import dayjs from 'dayjs'
import { z } from 'zod'

export enum PoTrackingStatus {
  Critical = 'critical',
  Urgent = 'urgent',
  Maybe = 'maybe',
  Follow = 'follow',
  Confirm = 'confirm',
  Done = 'done',
  Cancelled = 'cancelled',
}

export enum PoLogisticStatus {
  Producing = 'producing',
  Produced = 'produeced',
  Booked = 'booked',
  'In Transit' = 'in transit',
  Arrived = 'Arrived',
  Delay = 'Delay',
}

export const defaultTrackingPoSearchParamsFilter = {
  fromDate: dayjs().subtract(60, 'day').startOf('day').toDate(),
  toDate: dayjs().add(2, 'month').endOf('day').toDate(),
  trackingStatuses: enumKeys(PoTrackingStatus).map((key) => ({
    label: key,
    value: PoTrackingStatus[key],
  })),
}

// Search Params Schema
export const TrackingPoSearchParamsSchema = z.object({
  number: z.string().trim().optional(),
  fromDate: z.coerce.date().optional(),
  toDate: z.coerce.date().optional(),
  partyId: z.string().optional(),
  enableLogisticTracking: z.boolean().optional(),
  itemId: z.string().optional(),
  trackingStatuses: z.array(z.string()).default([]),
  pageNumber: z.number().optional().default(1),
  pageSize: z.number().optional().default(50),
  viewMode: z.enum(['head', 'item']).default('item'),
  displayRemainReceived: z.boolean().default(false),
})

export type TrackingPoSearchParams = z.infer<
  typeof TrackingPoSearchParamsSchema
>

/**
 * Tracking Po only
 */

// Search Result Model Schema
export const TrackingPoSearchResultModelSchema = z.object({
  id: z.number(),
  number: z.string(),
  date: z.coerce.date(),
  dueDate: z.coerce.date(),
  latestExpectedDueDate: z.coerce.date(),
  weekNo: z.number().int(),
  status: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  latestLogisticStatus: z.string().nullish(),
  enableLogisticTracking: z.boolean(),
  trackingStatus: z.string().nullable(),
  partyId: z.number().int(),
  hasAttachment: z.boolean(),
})

export type TrackingPoSearchResultModel = z.infer<
  typeof TrackingPoSearchResultModelSchema
>

// Search Result Schema
export const TrackingPoSearchResultSchema = z
  .object({
    data: z.array(TrackingPoSearchResultModelSchema),
  })
  .merge(BasePaginateResultSchema)

export type TrackingPoSearchResult = z.infer<
  typeof TrackingPoSearchResultSchema
>

/**
 * Tracking Po Item
 */
export const TrackingPoItemSearchResultModelSchema = z.object({
  id: z.number(),
  number: z.string(),
  date: z.coerce.date(),
  dueDate: z.coerce.date(),
  latestExpectedDueDate: z.coerce.date(),
  weekNo: z.number().int(),
  status: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  poItemId: z.number().int(),
  itemId: z.number().int(),
  qty: z.number(),
  outstanding: z.number(),
  latestLogisticStatus: z.string().nullish(),
  enableLogisticTracking: z.boolean(),
  trackingStatus: z.string().nullable(),
  partyId: z.number().int(),
  hasAttachment: z.boolean(),
})

export type TrackingPoItemSearchResultModel = z.infer<
  typeof TrackingPoItemSearchResultModelSchema
>

// Search Result Schema
export const TrackingPoItemSearchResultSchema = z
  .object({
    data: z.array(TrackingPoItemSearchResultModelSchema),
  })
  .merge(BasePaginateResultSchema)

export type TrackingPoItemSearchResult = z.infer<
  typeof TrackingPoItemSearchResultSchema
>
